import React, { FC, memo, useState, useEffect, useMemo, useContext } from 'react';
import { batch } from 'react-redux';
import { HandledComponentError } from '@components/ErrorHandling';
import { InvalidConfiguration } from '@components/InvalidConfiguration';
import { CarDetailsService, getOptionsList, CarJourneyType } from '../../../services';
import { useConfiguratorDuck } from '@hooks/useConfiguratorDuck';
import { useCarDetailsDuck } from '@hooks/useCarDetailsDuck';
import { useTranslations } from '@hooks/useTranslations';
import { useJourneyType } from '@hooks/useJourneyType';
import { useGTM } from '@hooks/useGTM';
import { useScrollToPositionOnMount } from '@hooks/useScrollToPositionOnMount';
import OrderPanel from '@components/v2/OrderPanel';
import { useDispatch, useSelector } from 'react-redux';
import { buildParameterizedPathname } from '@utils/url.utils';
import { setPageTitleDescriptionProperties } from '@utils/appConfig.utils';
import { BRAND, IS_B2B, ModalVersion } from '../../../constants/main';
import routes from '../../../constants/routes';
import Main from '@components/Layout/Main';
import { StyledGrid, ConfigLeftSection, ConfigRightSection, ConfigMainGrid } from '@components/Grid';
import { FEATURES_LIST, FeatureSwitchContext } from '../../../context/featureSwitchApp';
import { featuresToUrl } from '@utils/Configurator.utils';
import EngineSelect from '@components/v2/EngineSelect';
import InteriorSelect from '@components/v2/InteriorSelect';
import OptionsSelect from '@components/v2/OptionsSelect';
import Characteristics from '@components/v2/Characteristics';
import TitleWithBenefits from '@components/v2/TitleWithBenefits';
import { SC } from '../../../styles/theme';
import StockSlice from '../StockSlice';
import ImageSection from '@components/v2/ImageSection';
import ColorSelect from '@components/v2/ColorSelect';
import FAQs from '@components/FAQs';
import { useFinancePricesList } from '@hooks/usePriceCalculator';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { IConfiguratorOption, LastPartSelected } from '../../../redux/configurator/configurator.duck.interface';
import { Pages } from 'src/types/pages';
import CarDetailsDuck from 'src/redux/carDetails/carDetails.duck';
import UIDuck from 'src/redux/commonDucks/ui.duck';
import ConfiguratorDuck from 'src/redux/configurator/configurator.duck';
import { getAvailableOptionIds } from '@utils/options.utils';
import difference from 'lodash/difference';
import InterestedBox from '@components/v2/InterestedBox';
import { useRouter } from 'next/router';
import { isEmpty } from 'lodash';
import { getMatchingOptionsFromMandatoryOptsString } from '@utils/mandatoryOptions.utils';
import { ICarDetail } from '../../../redux/carDetails/carDetails.duck.interface';
import { useGlobalDuck } from '@hooks/useGlobalDuck';

export interface IConfigurator extends SC {
    initialFirstRender?: boolean;
}

const EXTERNAL_ID_SPLIT_CHARACTER = '+';

const ConfiguratorTemplate: FC<IConfigurator> = memo(({ className, initialFirstRender = true }) => {
    const dispatch = useDispatch();
    const { t } = useTranslations();
    const router = useRouter();
    const { paymentJourneyType } = useJourneyType();
    const { motorization, carOptions, selectedOptions, exteriorColor, interiorColor, externalId, lastPartSelected } =
        useConfiguratorDuck();
    const { carDetails } = useCarDetailsDuck();

    const mandatoryOptionsFs = useContext(FeatureSwitchContext)?.FEATURE_SWITCH_MANDATORY_OPTIONS ?? '';
    const mandatoryOptionIds = getMatchingOptionsFromMandatoryOptsString(mandatoryOptionsFs, carDetails?.lcdv16) ?? [];

    const { pushConfiguratorPageLoad, pushProductDetailImpression } = useGTM();
    const { handleScrollTo } = useScrollToPositionOnMount();

    const [firstRender, setFirstRender] = useState(initialFirstRender);
    const [showConfigurationInvalidError, setShowConfigurationInvalidError] = useState(
        !carDetails || !motorization || !interiorColor || !exteriorColor
    );
    const { carJourney } = useGlobalDuck();

    const selectedOptionsString = (selectedOptions ?? []).join();
    const [doNotUpdateOnOptionChange, setDoNotUpdateOnOptionChange] = useState(false);

    // APP-24860
    // needed when for configuration with option previous options(s) was/were moved to standard features
    // prevents from car-details request fail in such ocasions
    useEffect(() => {
        const optionsIds = getAvailableOptionIds(selectedOptions, carOptions);

        if (!isEmpty(difference(optionsIds, selectedOptions))) {
            dispatch(ConfiguratorDuck.setSelectedOptions(optionsIds));
        }
    }, [carOptions]);

    useEffect(() => {
        if (doNotUpdateOnOptionChange) {
            setDoNotUpdateOnOptionChange(false);
            return;
        }
        // when we switch motorization then the colors can have different slugs so use motorization as source of truth
        const exteriorColourSlug =
            lastPartSelected === LastPartSelected.MOTORIZATION && motorization?.exteriorColourSlug
                ? motorization.exteriorColourSlug
                : exteriorColor.exteriorColourSlug;
        const interiorColourSlug =
            lastPartSelected === LastPartSelected.MOTORIZATION && motorization?.interiorColourSlug
                ? motorization.interiorColourSlug
                : interiorColor.interiorColourSlug;
        const optionsIds = getAvailableOptionIds(selectedOptions, carOptions, mandatoryOptionIds);

        const updateCarDetails = (hasDifferentOptions = false): Promise<any> => {
            dispatch(UIDuck.setLoading(5));
            return CarDetailsService.getCarDetailsWithAllOptions(
                carDetails.nameplateBodyStyleSlug,
                carDetails.specPackSlug,
                motorization.engineGearboxFuelSlug || carDetails.engineGearboxFuelSlug,
                exteriorColourSlug,
                interiorColourSlug,
                hasDifferentOptions ? [] : router.query.reSaveOptionsOnVehicle ? selectedOptions : optionsIds,
                true
            )
                .then(({ vehicle, features }) => {
                    const q = location.search;

                    const newURL = featuresToUrl(router.asPath, paymentJourneyType, carJourney, {
                        q,
                        exteriorColourSlug: vehicle.exteriorColourSlug,
                        interiorColourSlug: vehicle.interiorColourSlug,
                        engineGearboxFuelSlug: vehicle.engineGearboxFuelSlug,
                        options: hasDifferentOptions
                            ? []
                            : router.query.reSaveOptionsOnVehicle
                            ? selectedOptions
                            : optionsIds,
                    });

                    batch(() => {
                        dispatch(CarDetailsDuck.setCurrentCarDetails(vehicle));
                        dispatch(ConfiguratorDuck.setExteriorColors(features.exteriorColors));
                        dispatch(ConfiguratorDuck.setInteriorColors(features.interiorColors));
                        dispatch(ConfiguratorDuck.setMotorizations(features.engines));
                    });

                    router.replace({ pathname: newURL.pathName, query: newURL.query }, undefined, {
                        shallow: true,
                    });

                    if (hasDifferentOptions) {
                        setDoNotUpdateOnOptionChange(true);
                        dispatch(ConfiguratorDuck.setSelectedOptions([]));
                    }

                    dispatch(UIDuck.setLoading(-5));

                    return vehicle;
                })
                .catch(() => {
                    dispatch(UIDuck.setLoading(-5));
                });
        };

        const updateOptions = (updatedCarDetails?: ICarDetail): Promise<any> => {
            dispatch(UIDuck.setOptionsLoading(5));
            // remove options from id
            let externalIdParts = (updatedCarDetails || carDetails)?.externalId?.split(EXTERNAL_ID_SPLIT_CHARACTER);
            let externalId = `${externalIdParts[0]}${EXTERNAL_ID_SPLIT_CHARACTER}${externalIdParts[1]}${EXTERNAL_ID_SPLIT_CHARACTER}${externalIdParts[2]}`;
            // update exterior and interior color in id
            externalIdParts = externalId?.split(EXTERNAL_ID_SPLIT_CHARACTER);
            if (motorization?.externalId) {
                externalIdParts[0] = motorization.externalId.split(EXTERNAL_ID_SPLIT_CHARACTER)[0];
            }
            externalIdParts[1] = exteriorColor.id;
            externalIdParts[2] = interiorColor.id;
            externalId = externalIdParts.join(EXTERNAL_ID_SPLIT_CHARACTER);

            return getOptionsList(externalId, optionsIds, paymentJourneyType)
                .then((optionsListResponse) => {
                    // let's update car images once we have fresh ones from options' endpoint
                    if (optionsListResponse.data?.images) {
                        dispatch(
                            CarDetailsDuck.setCurrentCarDetails({
                                ...(updatedCarDetails || carDetails),
                                images: optionsListResponse.data.images,
                            })
                        );
                    }
                    const responseOptions = optionsListResponse.data.options;
                    const responseOptionIds = responseOptions.map((o: IConfiguratorOption) => o.id);
                    dispatch(ConfiguratorDuck.setOptions(responseOptions));

                    if (router.query.optionsavailable) {
                        dispatch(
                            ConfiguratorDuck.setAvailableOptions((router.query.optionsavailable as string)?.split(','))
                        );
                    } else {
                        dispatch(
                            ConfiguratorDuck.setAvailableOptions(responseOptions?.map(({ id }: { id: string }) => id))
                        );
                    }
                    const noCompatibleSelectedOptionIds: string[] = [];
                    const compatibleSelectedOptionsIds: string[] = [];
                    optionsIds?.map((optId) =>
                        responseOptionIds?.includes(optId)
                            ? compatibleSelectedOptionsIds.push(optId)
                            : noCompatibleSelectedOptionIds.push(optId)
                    );

                    if (noCompatibleSelectedOptionIds?.length > 0) {
                        dispatch(ConfiguratorDuck.setSelectedOptions(compatibleSelectedOptionsIds));
                        dispatch(
                            UIDuck.openModal({
                                modalType: UIDuck.MODAL_TYPES.MISSING_PARTS,
                                data: {
                                    title: t('modal.invalidOptions.title'),
                                    message: t('modal.invalidOptions.description'),
                                    additionalMessage: noCompatibleSelectedOptionIds?.map(
                                        (optId) =>
                                            (carOptions?.find(
                                                (option) => option.id === optId
                                            )).title
                                    ),
                                    buttonText: t('modal.invalidOptions.button'),
                                },
                                modalVersion: ModalVersion.v2,
                                modalProperties: {
                                    centerModeOnDesktop: true,
                                },
                            })
                        );
                    }
                    dispatch(UIDuck.setOptionsLoading(-5));
                    // when changing the engine, we check if the new engine options has the previous one
                    return {
                        hasDifferentOptions:
                            optionsIds.length > 0 &&
                            optionsListResponse.data.externalId.split(EXTERNAL_ID_SPLIT_CHARACTER).length === 3,
                        shouldReloadDetails: optionsIds.reduce(
                            (res, optId) => responseOptionIds.includes(optId) && res,
                            true
                        ),
                    };
                })
                .catch(() => {
                    dispatch(UIDuck.setOptionsLoading(-5));
                });
        };

        if (optionsIds.length > 0) {
            updateOptions().then((res) => {
                if (res.shouldReloadDetails) updateCarDetails(res.hasDifferentOptions);
            });
        } else {
            updateCarDetails().then((updatedCarDetails) => {
                updateOptions(updatedCarDetails);
            });
        }
    }, [exteriorColor, interiorColor, motorization, selectedOptionsString, externalId]);

    useEffect(() => {
        window.scrollTo(0, 0);
        dispatch(UIDuck.openCharacteristics({ data: {}, modalType: '' }));
        setFirstRender(false);
    }, []);

    useEffect(() => {
        if (!firstRender) handleScrollTo();
    }, [firstRender, handleScrollTo]);
    useFinancePricesList();

    useEffect(() => {
        if (carDetails.stock !== undefined) {
            pushConfiguratorPageLoad(paymentJourneyType, carDetails);
            pushProductDetailImpression('configurator', carDetails, false);
        }
    }, [carDetails.stock]);

    const fullBrand = t('title');
    const pageTitleDescriptionProperties = setPageTitleDescriptionProperties(fullBrand, carDetails);

    const pageTitle = t(`seo.configPage.${IS_B2B ? 'b2b' : paymentJourneyType}.title`, pageTitleDescriptionProperties);

    const pageDescription = t(
        `seo.configPage.${IS_B2B ? 'b2b' : paymentJourneyType}.description`,
        pageTitleDescriptionProperties
    );

    const pageCanonicalLink = buildParameterizedPathname(routes.CAR, carJourney, {
        nameplateBodyStyleSlug: carDetails.nameplateBodyStyleSlug,
        speckPackSlug: carDetails.specPackSlug,
        engineGearboxFuelSlug: carDetails.engineGearboxFuelSlug,
        exteriorColourSlug: carDetails.exteriorColourSlug,
        interiorColourSlug: carDetails.interiorColourSlug,
    });

    const isStockSliceEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_IS_STOCK_SLICE_ENABLED);

    try {
        if (showConfigurationInvalidError) {
            return <InvalidConfiguration />;
        }

        return (
            <div className={className}>
                <Main title={pageTitle} description={pageDescription} canonicalLink={pageCanonicalLink} whiteBackground>
                    <OrderPanel className="order-panel-top" page={Pages.car} isWide />
                    <StyledGrid brand={BRAND} noPadding isWide>
                        <ConfigMainGrid isWide>
                            <ConfigLeftSection>
                                <ImageSection />
                            </ConfigLeftSection>
                            <ConfigRightSection unsetMaxWidth>
                                <TitleWithBenefits />
                                <EngineSelect />
                                <ColorSelect />
                                <InteriorSelect />
                                <OptionsSelect />
                                <InterestedBox fullWidth isLegalText page={Pages.car} />
                            </ConfigRightSection>
                        </ConfigMainGrid>
                    </StyledGrid>
                    {isStockSliceEnabled && <StockSlice normalizeEnabled={false} />}
                    <div id="characteristics">
                        <Characteristics />
                    </div>
                    <FAQs />
                </Main>
            </div>
        );
    } catch (e: any) {
        return <HandledComponentError error={e} />;
    }
});
ConfiguratorTemplate.displayName = 'ConfiguratorTemplate';
export default ConfiguratorTemplate;
